import { useMemo, useState, useCallback, useEffect } from 'react';
import { useLots, useCreateLot, useUpdateLot } from '../../../../hooks/useLots';
import { useProjects } from '../../../../hooks/useProjects';
import { useContractsWithSubscribersAll } from '../../../../hooks/useContracts';
import { useConstructionTypes } from '../../../../hooks/useConstructionTypes';
import { INITIAL_LOT_STATE } from '../constants/lotsConstants';

export const useLotPlan = (uuidAllotment) => {
  const { data: lots = [], isLoading } = useLots({ uuid_allotment: uuidAllotment });
  const { data: allLots = [] } = useLots();
  const { data: projects = [] } = useProjects();
  const { data: contracts = [] } = useContractsWithSubscribersAll();
  const { data: constructionTypes = [] } = useConstructionTypes();
  const createMutation = useCreateLot();
  const updateMutation = useUpdateLot();

  const [selectedLot, setSelectedLot] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftLot, setDraftLot] = useState({ ...INITIAL_LOT_STATE, uuid_allotment: uuidAllotment });
  const [localLots, setLocalLots] = useState([]);

  useEffect(() => {
    setDraftLot((prev) => ({ ...prev, uuid_allotment: uuidAllotment }));
  }, [uuidAllotment]);

  useEffect(() => {
    setLocalLots(lots);
  }, [lots]);

  const handleOpenCreate = () => {
    setDraftLot({ ...INITIAL_LOT_STATE, uuid_allotment: uuidAllotment, pos_x: 30, pos_y: 30 });
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleCreateLot = async (lotData) => {
    if (!uuidAllotment) return;
    try {
      const payloadSource = lotData || draftLot;
      const payload = {
        ...payloadSource,
        uuid_allotment: uuidAllotment,
        pos_x: payloadSource?.pos_x ?? 30,
        pos_y: payloadSource?.pos_y ?? 30,
      };
      const created = await createMutation.mutateAsync(payload);
      if (created?.uuid) {
        const newLot = { ...payload, ...created };
        setLocalLots((prev) => [...prev, newLot]);
        setSelectedLot(newLot);
        setDetailsOpen(true);
      }
      setCreateOpen(false);
    } catch (error) {
      console.error('Error creating lot in plan:', error);
    }
  };

  const handleSelectLot = (lot) => {
    setSelectedLot(lot);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedLot(null);
  };

  const handleUpdateLot = useCallback(
    async (updatedLot) => {
      if (!updatedLot?.uuid) return;
      setLocalLots((prev) =>
        prev.map((lot) => (lot.uuid === updatedLot.uuid ? { ...lot, ...updatedLot } : lot))
      );
      try {
        await updateMutation.mutateAsync({ id: updatedLot.uuid, lotData: updatedLot });
      } catch (error) {
        console.error('Error updating lot:', error);
      }
    },
    [updateMutation]
  );

  const selectedProject = useMemo(() => {
    if (selectedLot?.project) {
      return selectedLot.project;
    }
    if (!selectedLot?.uuid) return null;
    return projects.find((proj) => proj.uuid_lot === selectedLot.uuid) || null;
  }, [selectedLot, projects]);

  const selectedContract = useMemo(() => {
    if (selectedLot?.contract) {
      return selectedLot.contract;
    }
    if (!selectedLot?.uuid_contract) return null;
    return contracts.find((contract) => contract.uuid === selectedLot.uuid_contract) || null;
  }, [selectedLot, contracts]);
  const subscribers = selectedContract?.subscribers || [];


  const handleDragEnd = useCallback(
    (lot, position) => {
      if (!lot || !position) return;
      handleUpdateLot({
        ...lot,
        pos_x: Math.round(position.x ?? 0),
        pos_y: Math.round(position.y ?? 0),
      });
    },
    [handleUpdateLot]
  );

  const lotsById = useMemo(() => {
    return localLots.reduce((acc, lot) => {
      acc[lot.uuid] = lot;
      return acc;
    }, {});
  }, [localLots]);

  return {
    lots: localLots,
    allLots,
    isLoading,
    projects,
    contracts,
    constructionTypes,
    selectedLot,
    detailsOpen,
    createOpen,
    draftLot,
    setDraftLot,
    lotsById,
    handleOpenCreate,
    handleCloseCreate,
    handleCreateLot,
    handleSelectLot,
    handleCloseDetails,
    handleUpdateLot,
    handleDragEnd,
    selectedProject,
    selectedContract,
    subscribers,
  };
};
